import { PayloadAction } from "@reduxjs/toolkit";
import { put, call, takeLatest } from "redux-saga/effects";
import { toast } from "react-toastify";
import { setAccessToken, setRememberMe, removeRememberMe, getRememberMe, setRole, setOptimiseRoutesAccess } from "@/core/token";
import { authActions } from "./authSlice";
import authApi from "@/api/auth";
import { AuthCodePayload, LoginPayload, SetNewPasswordPasswordPayload } from "@/shared/interfaces";
import { toError } from "@/shared/utils/toError";
import userApi from "@/api/setting/user";
import { ResetPasswordRequestPayload, SetNewResetPasswordPayload } from "@/shared/interfaces/auth";

function handleRemember(email: string, remember?: boolean) {
  if (!remember) {
    const existRemember = getRememberMe();
    if (!existRemember) return;
    removeRememberMe();
    return;
  }
  setRememberMe(email, remember);
}

function* handleLogin(action: PayloadAction<LoginPayload>) {
  const { email, password, remember } = action.payload;
  try {
    const { data } = yield call(authApi.login, { email, password });
    if (!data.authorizationToken) {
      yield put(authActions.reset());
      throw Error("Wrong Password");
    }
    handleRemember(email, remember);
    yield put(authActions.loginSuccess(data));
  } catch (error) {
    const err = toError(error);
    yield put(authActions.requestFailed());
    toast.error(err.message);
  }
}

function* handleVerifyAuthCode(action: PayloadAction<AuthCodePayload & { navigate: (path: string) => void }>) {
  const { authorizationToken, authCode, navigate } = action.payload;
  try {
    const { data } = yield call(authApi.verifyCode, { authCode, authorizationToken });
    if (!data.accessToken) {
      yield put(authActions.requestFailed());
      throw Error("Login Failed");
    }
    setAccessToken(data.accessToken);

    const { data: result } = yield call(userApi.getMyInfo);
    // yield put(authActions.getMyselfSuccess(result));
    setRole(result.role);
    setOptimiseRoutesAccess(result.canAccessOptimiseRoutes ?? false);

    yield put(authActions.verifyAuthCodeSuccess());
    //redirect after Verify success
    if (navigate) navigate("/deliveries");
  } catch (error) {
    const err = toError(error);
    yield put(authActions.requestFailed());
    toast.error(err.message);
  }
}

function* handleSetNewPassword(action: PayloadAction<SetNewPasswordPasswordPayload & { navigate: (path: string) => void }>) {
  const { authorizationToken, password, navigate } = action.payload;
  try {
    const { data } = yield call(authApi.setNewPassword, { password, authorizationToken });
    if (!data.accessToken) {
      yield put(authActions.requestFailed());
      throw Error("Set Password Failed");
    }
    setAccessToken(data.accessToken);
    yield put(authActions.setNewPasswordSuccess());
    //redirect after Set New Password success
    if (navigate) navigate("/deliveries");
  } catch (error) {
    const err = toError(error);
    yield put(authActions.requestFailed());
    toast.error(err.message);
  }
}

function* handleSetNewResetPassword(action: PayloadAction<SetNewResetPasswordPayload & { navigate: (path: string) => void }>) {
  const { token, password, navigate } = action.payload;
  try {
    yield call(authApi.setNewResetPassword, { password, token });
    yield put(authActions.setNewResetPasswordSuccess());
    //redirect after Set New Password success
    toast.success("Password has been reset");
    if (navigate) navigate("/login");
  } catch (error) {
    const err = toError(error);
    yield put(authActions.requestFailed());
    toast.error(err.message);
  }
}

function* handleResetPasswordRequest(action: PayloadAction<ResetPasswordRequestPayload & { navigate: (path: string) => void }>) {
  const { email, navigate } = action.payload;
  try {
    const { data } = yield call(authApi.resetPasswordRequest, { email });
    if (data.statusCode !== 200) {
      yield put(authActions.requestFailed());
      throw Error("Please check your details");
    }
    yield put(authActions.resetPasswordRequestSuccess());
    toast.success("Reset link has been sent to your email");
    if (navigate) navigate("/login");
  } catch (error) {
    const err = toError(error);
    yield put(authActions.requestFailed());
    toast.error(err.message);
  }
}

export default function* watchAuthSaga() {
  yield takeLatest(authActions.login.type, handleLogin);
  yield takeLatest(authActions.verifyAuthCode.type, handleVerifyAuthCode);
  yield takeLatest(authActions.setNewPassword.type, handleSetNewPassword);
  yield takeLatest(authActions.resetPasswordRequest.type, handleResetPasswordRequest);
  yield takeLatest(authActions.setNewResetPassword.type, handleSetNewResetPassword);
}
